/**
 * Tipo de cambio escrito por el usuario, leído sin adivinar (revisión 2026-10-10).
 *
 * Lógica pura y sin dependencias: la usan Crear CFDI (cfdi/tipocambio.js), el
 * complemento de pago (cfdi-pagos.js) y, por window.FdTasa (app.js), las vistas
 * con script en línea (Emitidos › Aplicar pago, Timbrado masivo y por
 * referencia). El servidor aplica la MISMA regla y no confía en el navegador:
 * application/libraries/Tasa_capturada.php.
 *
 * Por qué: con inputmode="decimal" un iPhone en región de coma decimal ofrece
 * "17,5". Quitar todas las comas lo volvía 175, y parseFloat se tragaba lo que
 * sobrara ("17.5,3" → 17.5; "17,5" → 17).
 *
 *   "17.5" / "17"     → punto decimal, como siempre.
 *   "17,5"            → una sola coma y ningún punto: es el decimal (17.5).
 *   "1,234.56"        → comas de miles bien agrupadas con punto decimal
 *                       (number_format() del servidor): se respetan.
 *   otra mezcla       → 'ambigua' ("1.234,5", "17.5,3", "1,2,3", "1,234,567",
 *                       "1,23.4", "17.5abc", ",5"…): no se interpreta.
 *   sin cifras        → 'no_numerica' ("abc"): se trata como no capturada.
 *   vacía / ≤ 0       → 'vacia' / 'no_positiva'.
 */

function sin(estado) {
  return { estado: estado, numero: null, texto: '' };
}

/**
 * @returns {{estado: 'vacia'|'no_numerica'|'ambigua'|'no_positiva'|'valida', numero: number|null, texto: string}}
 *          texto = la tasa con punto decimal y sin miles (lo que viaja al PAC).
 */
export function interpretarTasa(valor) {
  if (valor === null || valor === undefined) return sin('vacia');
  // Un número de JS no tiene comas que interpretar.
  if (typeof valor === 'number') {
    if (!isFinite(valor) || valor <= 0) return sin('no_positiva');
    var t = String(valor);
    if (/e/i.test(t)) t = valor.toFixed(10).replace(/0+$/, '').replace(/\.$/, '');
    return { estado: 'valida', numero: valor, texto: t };
  }

  var crudo = String(valor).trim();
  if (crudo === '') return sin('vacia');
  if (!/\d/.test(crudo)) return sin('no_numerica');

  var texto = null;
  if (/^-?\d+(\.\d+)?$/.test(crudo)) {
    texto = crudo;                                   // 17 · 17.5 · -3 (cae abajo)
  } else if (/^\.\d+$/.test(crudo)) {
    texto = '0' + crudo;                             // .12 → 0.12 (xs:decimal lo acepta)
  } else if (/^\d+\.$/.test(crudo)) {
    texto = crudo.slice(0, -1);                      // 17. → 17 (xs:decimal lo acepta)
  } else if (/^\d+,\d+$/.test(crudo)) {
    texto = crudo.replace(',', '.');                 // 17,5 → 17.5
  } else if (/^\d{1,3}(,\d{3})+\.\d+$/.test(crudo)) {
    texto = crudo.replace(/,/g, '');                 // 1,234.56 → 1234.56
  }
  if (texto === null) return sin('ambigua');

  var n = parseFloat(texto);
  if (!isFinite(n) || n <= 0) return sin('no_positiva');
  return { estado: 'valida', numero: n, texto: texto };
}

/** La tasa como número usable; null si no lo es. */
export function normalizarTasa(valor) {
  return interpretarTasa(valor).numero;
}

/**
 * Aviso para una tasa ambigua; null si no aplica. Repite lo capturado sólo si
 * son cifras, puntos, comas, espacios o signos: algunos diálogos pintan HTML.
 */
export function avisoTasaAmbigua(valor) {
  if (interpretarTasa(valor).estado !== 'ambigua') return null;
  var crudo = String(valor).trim();
  var muestra = /^[0-9.,\s+-]{1,30}$/.test(crudo) ? ' «' + crudo + '»' : '';
  return 'No se entiende el tipo de cambio' + muestra + '. ' +
    'Escríbelo con punto decimal y sin separador de miles, por ejemplo 17.5.';
}

/**
 * Lo que se manda al servidor: la tasa leída si es válida ("17,5" → "17.5");
 * si no, lo capturado tal cual, para que el servidor —que aplica la misma
 * regla— conteste con su mensaje (vacía, ambigua…). Nunca se inventa un valor.
 */
export function tasaParaEnviar(valor) {
  var leida = interpretarTasa(valor);
  if (leida.estado === 'valida') return leida.texto;
  return (valor === null || valor === undefined) ? '' : String(valor);
}

/** ¿Exactamente 1 a 6 decimales? (como number_format(round(n, 6), 6) en el servidor) */
function esUno(n) {
  return Math.round(n * 1e6) === 1e6;
}

/**
 * Error para el tipo de cambio de una divisa (moneda distinta de MXN y XXX), o
 * null si se puede timbrar. La misma regla que el servidor
 * (Restclient::tipo_cambio_comprobante) y que #349 en el complemento de pago:
 * ambigua → cómo escribirla; vacía, sin cifras, ≤ 0 o exactamente 1 → falta el
 * tipo de cambio de esa divisa. Un tipo de cambio oficial de 1.000000 contra el
 * peso no existe: 1 con divisa es lo que rellenaba Timbrado masivo sin tasa.
 */
export function errorTasaDivisa(moneda, valor) {
  var m = String(moneda || '').trim().toUpperCase();
  if (!m || m === 'MXN' || m === 'XXX') return null;
  var leida = interpretarTasa(valor);
  if (leida.estado === 'ambigua') return avisoTasaAmbigua(valor);
  if (leida.estado === 'valida' && !esUno(leida.numero)) return null;
  return 'Falta el tipo de cambio del ' + m + ': captura el del día (el que publica el DOF). ' +
    'Un CFDI en ' + m + ' no se timbra sin él ni con 1.00.';
}

/**
 * La tasa del día que devolvió obtener_tipo_cambio_ajax, lista para prellenar
 * un campo; '' si no hay una que sirva (sin tasa, 'inf', ilegible o 1). Nunca
 * "1": el campo queda vacío y la pantalla pide capturarlo.
 */
export function tasaDeConsulta(resp) {
  var r = resp || {};
  if (String(r.code) !== '200') return '';
  var crudo = (r.rate !== undefined && r.rate !== null && r.rate !== '') ? r.rate : r.tipocambio;
  var leida = interpretarTasa(crudo);
  if (leida.estado !== 'valida' || esUno(leida.numero)) return '';
  return leida.texto;
}
